import { ActiveToast, IndividualConfig } from 'ngx-toastr';
import { CommonBroadcastService } from '@caslink/common-client/ng-app/common-broadcast/common-broadcast.service';
import { ControlService } from '../../control/control.service';
import { DashboardCacheService } from './../../dashboard/dashboard-cache.service';
import { DashboardUpdateActions } from './dashboard-update-actions.service';
import { EventTypes } from '@caslink/common-client/src';
import { IDashboardChangeConfirmationData } from '../models/models';
import { Injectable } from '@angular/core';
import { ITypedResponse } from '@caslink/common-client';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { ToastrMainService, ToastrStatus } from './toastr-main.service';
import { ViewModelUpdateInfo } from '@caslink/common-client/ng-app/alerts/typings/ViewModelUpdateInfo';

interface ActiveMessage extends ExtendedActiveMessage {
    status: ToastrStatus;
    toastr: ActiveToast<any>;
};

interface ExtendedActiveMessage {
    backendResultSubject?: Subject<void>,
    errorTimeout?: number;
}

type ConfigurationName = 'requestError' | 'default' | 'success' | 'timeoutError';

type ToastrConfiguration = {
    [key in ConfigurationName]: (deviceData: DeviceData) => ConfigurationProps;
};

interface DeviceData {
    areaName: string;
    deviceName: string;
    hardwareItemId: number;
    locationName: string;
};

interface ConfigurationProps {
    individualConfiguration: Partial<IndividualConfig>;
    message: string;
    status: ToastrStatus;
    title: string;
}

const SHOW_TIMEOUT_ERROR_AFTER_SECONDS = 90;
const TOASTR_DEFAULT_HIDE_AFTER_SECONDS = 90;
const TOASTR_ERROR_HIDE_AFTER_SECONDS = 10;
const TOASTR_SUCCESS_HIDE_AFTER_SECONDS = 10;

@Injectable({
    providedIn: 'root',
})
export class DashboardToastrConfirmationService {
    devices = new Map<number, { deviceData: DeviceData, messages: ActiveMessage[] }>();
    signalrResponseSubscription: Subscription;

    toastrConfiguration: ToastrConfiguration = {
        requestError: (deviceData) => {
            return {
                title: `${deviceData.locationName} / ${deviceData.areaName}`,
                message: `${deviceData.deviceName} control updates failed.`,
                status: ToastrStatus.ERROR,
                individualConfiguration: {
                    timeOut: TOASTR_ERROR_HIDE_AFTER_SECONDS * 1000,
                    progressBar: false,
                },
            };
        },
        default: (deviceData) => {
            return {
                title: `${deviceData.locationName} / ${deviceData.areaName}`,
                message: `${deviceData.deviceName} control updates pending...`,
                status: ToastrStatus.DEFAULT,
                individualConfiguration: {
                    timeOut: TOASTR_DEFAULT_HIDE_AFTER_SECONDS * 1000,
                    progressBar: true,
                    progressAnimation: 'increasing',
                    disableTimeOut: 'extendedTimeOut',
                },
            };
        },
        success: (deviceData) => {
            return {
                title: `${deviceData.locationName} / ${deviceData.areaName}`,
                message: `${deviceData.deviceName} control updates completed.`,
                status: ToastrStatus.SUCCESS,
                individualConfiguration: {
                    timeOut: TOASTR_SUCCESS_HIDE_AFTER_SECONDS * 1000,
                    progressBar: false,
                },
            };
        },
        timeoutError: (deviceData) => {
            return {
                title: `${deviceData.locationName} / ${deviceData.areaName}`,
                message: `${deviceData.deviceName} control update confirmation timed out.`,
                status: ToastrStatus.WARNING,
                individualConfiguration: {
                    timeOut: TOASTR_ERROR_HIDE_AFTER_SECONDS * 1000,
                    progressBar: false,
                },
            };
        },
    };

    constructor(
        private commonBroadcastService: CommonBroadcastService,
        private controlService: ControlService,
        private dashboardCacheService: DashboardCacheService,
        private dashboardUpdateActions: DashboardUpdateActions,
        private toastrMainService: ToastrMainService,
    ) {
    }

    async update(data: IDashboardChangeConfirmationData) {
        const backendResultSubject = new ReplaySubject<void>();
        await this.createDevice(data);
        this.subscribeToDeviceResponse();
        const message = this.createToastrMessage(data.hardwareItemId, 'default', {
            backendResultSubject,
            ...this.setActions(data.hardwareItemId),
        });
        this.updateDevice(data, message);
        return backendResultSubject;
    }

    private getConfiguration(configurationName: string, deviceData: DeviceData) {
        return this.toastrConfiguration[configurationName](deviceData);
    }

    private createToastr(hardwareItemId: number, configuration: ConfigurationProps) {
        const message = {
            title: configuration.title,
            text: configuration.message,
            status: configuration.status,
        };
        const toastr = this.toastrMainService.show(message, configuration.individualConfiguration);
        this.setToastrOnHideAction(hardwareItemId, toastr);
        this.setToastrOnShowAction(toastr);
        return toastr;
    }

    private setToastrOnHideAction(hardwareItemId: number, toastr: ActiveToast<any>) {
        toastr.onHidden.subscribe(() => {
            this.removeToastrAtDevice(hardwareItemId, toastr.toastId);
            this.unsubscribe();
        });
    }

    private setToastrOnShowAction(toastr: ActiveToast<any>) {
        // Hovering over toastr extends the timeout value which is unwanted behaviour. This ensure toastr close at given time.
        setTimeout(() => {
            toastr.toastRef.manualClose();
        }, 1000 * TOASTR_DEFAULT_HIDE_AFTER_SECONDS);
    }

    private removeToastrAtDevice(hardwareItemId: number, toastrId: number) {
        const messages = this.devices.get(hardwareItemId)?.messages;
        const messageIndex = messages.findIndex(message => message.toastr.toastId === toastrId);

        if (messageIndex !== -1) {
            messages.splice(messageIndex, 1);
        }
    }

    private sendDeviceEvent(data: IDashboardChangeConfirmationData) {
        this.controlService.broadcastCardStatusUpdate({
            locationId: data.locationId,
            hardwareItemId: data.hardwareItemId,
            status: 'Transmitted',
        });
    }

    private updateDevice(data: IDashboardChangeConfirmationData, message: ActiveMessage) {
        this.patchNote(data);
        this.dashboardUpdateActions.update(data)
            .then((response: ITypedResponse<string>) => {
                if (response?.success) {
                    this.sendDeviceEvent(data);
                } else {
                    this.backendError(data, message);
                }
            }).catch(() => {
                this.backendError(data, message);
            })
            .finally(() => {
                message.backendResultSubject.complete();
            });
    }

    private backendError(data: IDashboardChangeConfirmationData, message: ActiveMessage) {
        message.toastr.toastRef.manualClose();
        this.clearActions(message);
        this.createToastrMessage(data.hardwareItemId, 'requestError');
    }

    // We are unable to identify which signalR response belongs to which toast since there can be more updates at once.
    // Our most educated guess is to close the oldest message displayed.
    private closeOldestDefaultMessage(hardwareItemId: number) {
        const messages = this.devices.get(hardwareItemId).messages;
        const oldestMessage = messages.find(message => message.status === ToastrStatus.DEFAULT);
        this.clearActions(oldestMessage);
        oldestMessage?.toastr.toastRef.manualClose();
    }

    private subscribeToDeviceResponse() {
        if (!this.signalrResponseSubscription) {
            this.signalrResponseSubscription = this.commonBroadcastService
                .on(EventTypes.ViewModelUpdate)
                .subscribe((data: ViewModelUpdateInfo) => {
                    /* ViewModel is boolean for confirmation updates and a complex object for non-confirmation updates. ¯\_(ツ)_/¯ */
                    const isPositiveDeviceResponse = typeof data?.viewModel === 'boolean' && data?.entityId;
                    if (isPositiveDeviceResponse) {
                        this.closeOldestDefaultMessage(data.entityId);
                        this.createToastrMessage(data.entityId, 'success');
                    }
                });
        }
    }

    private unsubscribe() {
        if (!this.hasPendingMessages()) {
            this.signalrResponseSubscription.unsubscribe();
        }
    }

    private hasPendingMessages() {
        for (const [_, deviceData] of this.devices) {
            if (deviceData.messages.length > 1) {
                return true;
            }
        }
        return false;
    }

    private patchNote(data: IDashboardChangeConfirmationData) {
        data.deviceInfo.$deviceHierarchy.note = data.note;
    }

    // When we get no response from SignalR websocket we can safely assume
    // that the device was not updated. But we still need update the toast status.
    // Also device update can take significant amout of time (unknown time).
    private setActions(hardwareItemId: number) {
        return {
            errorTimeout: window.setTimeout(() => {
                this.createToastrMessage(hardwareItemId, 'timeoutError');
            }, SHOW_TIMEOUT_ERROR_AFTER_SECONDS * 1000),
        };
    }

    private clearActions(message: ActiveMessage) {
        clearTimeout(message?.errorTimeout);
    }

    private createToastrMessage(hardwareItemId: number, configurationName: ConfigurationName, extendedConfiguration?: ExtendedActiveMessage) {
        const device = this.devices.get(hardwareItemId);
        const configuration = this.getConfiguration(configurationName, device.deviceData);
        const toastr = this.createToastr(hardwareItemId, configuration);

        const message: ActiveMessage = {
            toastr,
            status: configuration.status,
            ...extendedConfiguration,
        };
        device.messages.push(message);
        return message;
    }

    private async createDevice(data: IDashboardChangeConfirmationData) {
        if (!this.devices.get(data.hardwareItemId)) {
            this.devices.set(data.hardwareItemId, {
                deviceData: await this.loadDeviceData(data),
                messages: [],
            });
        }
        return this.devices.get(data.hardwareItemId);
    }

    private async loadDeviceData(data: IDashboardChangeConfirmationData) {
        return {
            areaName: await this.getDeviceAreaName(data.locationId, data.hardwareItemId),
            locationName: await this.getLocationName(data.locationId),
            deviceName: data.name,
            hardwareItemId: data.hardwareItemId,
        } as DeviceData;
    }

    private getLocationName(locationId: number) {
        return this.dashboardCacheService.getLocationName(locationId) ?? 'Unknown location name';
    }

    private getDeviceAreaName(locationId: number, hardwareItemId: number) {
        return this.dashboardCacheService.getDeviceAreaName(locationId, hardwareItemId) ?? 'Unknown area name';
    }
}
